// Rules for the error-handling ruleset feature (ERROR-HANDLING-UI-PLAN §10).
//
//   14.1 — inline ruleset content (per ServiceTask node)
//   14.2 — $ref integrity (a node binding must resolve to a project ruleset)
//   14.3 — unused project ruleset (referenced by 0 nodes)
//   14.4 — a thrown error code with no Error Boundary Event to catch it (warning)
//
// 14.2/14.3 need the project ruleset library, which arrives via ctx.ruleSets;
// they no-op when no library is present (e.g. a plain file with inline rules).
import { registerRule } from '../registry';
import { ValidationRule, ValidationIssue } from '../types';
import { labelOf } from '../helpers';
import { NodeErrorHandling } from '../../../types';
import { validateRuleSet, getNodeRef, getNodeRules, collectThrownCodes } from '../../errorRuleset';

const rule_14_1_RuleSetContent: ValidationRule = {
  id: '14.1',
  description: 'ServiceTask error-handling ruleset content (throw codes, retry on-exhausted, status tokens, backoff, predicates)',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      const eh = (n.data as any)?.errorHandling as NodeErrorHandling | undefined;
      if (!eh) continue;
      const rules = getNodeRules(eh);
      for (const issue of validateRuleSet(rules)) {
        issues.push({
          severity: issue.severity,
          rule: '14.1',
          message: `${labelOf(n)}: ${issue.ruleIndex >= 0 ? `rule ${issue.ruleIndex + 1} — ` : ''}${issue.message}`,
          processId: process.id,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_14_1_RuleSetContent);

const rule_14_2_RefIntegrity: ValidationRule = {
  id: '14.2',
  description: 'A node bound to a project ruleset ($ref) must resolve to an existing ruleset',
  scope: 'diagram',
  run: (ctx) => {
    const library = ctx.ruleSets;
    if (!library || library.length === 0) {
      // Without a library we cannot resolve refs. If any node carries a $ref
      // we still surface it as a dangling binding so it is not silently lost.
      const ids = new Set<string>();
      const issues: ValidationIssue[] = [];
      for (const proc of ctx.processes) {
        for (const n of proc.nodes) {
          const ref = getNodeRef((n.data as any)?.errorHandling);
          if (ref && !ids.has(`${proc.id}:${n.id}`)) {
            issues.push({
              severity: 'error',
              rule: '14.2',
              message: `${labelOf(n)} binds to ruleset "${ref}" but no project ruleset library is loaded.`,
              processId: proc.id,
              nodeIds: [n.id],
            });
          }
        }
      }
      return issues;
    }
    const known = new Set(library.map(r => r.id));
    const issues: ValidationIssue[] = [];
    for (const proc of ctx.processes) {
      for (const n of proc.nodes) {
        const ref = getNodeRef((n.data as any)?.errorHandling);
        if (ref && !known.has(ref)) {
          issues.push({
            severity: 'error',
            rule: '14.2',
            message: `${labelOf(n)} binds to ruleset "${ref}" which no longer exists in the project.`,
            processId: proc.id,
            nodeIds: [n.id],
          });
        }
      }
    }
    return issues;
  },
};
registerRule(rule_14_2_RefIntegrity);

const rule_14_3_UnusedRuleSet: ValidationRule = {
  id: '14.3',
  description: 'A project ruleset referenced by zero nodes is dead weight',
  scope: 'diagram',
  run: (ctx) => {
    const library = ctx.ruleSets;
    if (!library || library.length === 0) return [];
    const used = new Set<string>();
    for (const proc of ctx.processes) {
      for (const n of proc.nodes) {
        const ref = getNodeRef((n.data as any)?.errorHandling);
        if (ref) used.add(ref);
      }
    }
    return library
      .filter(rs => !used.has(rs.id))
      .map(rs => ({
        severity: 'warning' as const,
        rule: '14.3',
        message: `Project ruleset «${rs.name}» is not referenced by any node.`,
      }));
  },
};
registerRule(rule_14_3_UnusedRuleSet);

const rule_14_4_UncaughtThrow: ValidationRule = {
  id: '14.4',
  description: 'A ServiceTask ruleset throws an error code with no Error Boundary Event (catch-all or matching) to catch it',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];

    // Index Error Boundary Events by the activity they are attached to.
    // Catch-all = ErrorBoundaryEvent with empty/undefined exceptionType.
    const caughtByNode = new Map<string, { catchAll: boolean; codes: Set<string> }>();
    for (const n of process.nodes) {
      if (n.type !== 'ErrorBoundaryEvent') continue;
      const parentId = (n as any).parentNode as string | undefined;
      if (!parentId) continue;
      const entry = caughtByNode.get(parentId) || { catchAll: false, codes: new Set<string>() };
      const code = ((n.data as any)?.exceptionType ?? '').toString().trim();
      if (code === '') entry.catchAll = true;
      else entry.codes.add(code);
      caughtByNode.set(parentId, entry);
    }

    for (const n of process.nodes) {
      const eh = (n.data as any)?.errorHandling as NodeErrorHandling | undefined;
      if (!eh) continue;
      const thrown = collectThrownCodes(getNodeRules(eh));
      if (thrown.length === 0) continue;
      const caught = caughtByNode.get(n.id);
      if (caught?.catchAll) continue; // catch-all handles everything
      const uncaught = thrown.filter(code => !caught?.codes.has(code));
      if (uncaught.length === 0) continue;
      issues.push({
        severity: 'warning',
        rule: '14.4',
        message: `${labelOf(n)} can throw ${uncaught.map(c => `"${c}"`).join(', ')} but no Error Boundary Event on it catches ${uncaught.length === 1 ? 'it' : 'them'} (add a matching or catch-all error boundary).`,
        processId: process.id,
        nodeIds: [n.id],
      });
    }
    return issues;
  },
};
registerRule(rule_14_4_UncaughtThrow);
